import { useState } from 'react';

import { SettingsPageFrame } from '@eluve/blocks';
import { Button, Caption, Stack, Textarea, useToast } from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import { useBeforeUnload } from '@eluve/utility-hooks';

import { useChatSettings } from './useChatSettings';

const CHAT_EXAMPLE_INSTRUCTION_KEYS = [
  'settings.chat.exampleRemoveParts',
  'settings.chat.exampleIncludeDiscussion',
  'settings.chat.exampleMoreConcise',
  'settings.chat.exampleMoreDetailsPlan',
] as const;

export const ChatCustomInstructionsPage = () => {
  const { customInstructions, saveInstructions } = useChatSettings();
  const toast = useToast();
  const { t } = useTranslation();

  const [value, setValue] = useState(customInstructions);
  const [isSaving, setIsSaving] = useState(false);

  const isDirty = value !== customInstructions;
  useBeforeUnload(isDirty);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await saveInstructions(value);
    } catch {
      toast.error(t('settings.chat.saveInstructionsFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SettingsPageFrame
      title={t('settings.chat.customInstructionsPageTitle')}
      parentTitle={t('settings.chat.title')}
      backTo=".."
      description={t('settings.note.customInstructionsDescription')}
    >
      <Stack gap={4} align="stretch">
        <div className="overflow-hidden rounded-md border border-border-primary bg-bg-primary focus-within:ring-1 focus-within:ring-border-secondary">
          <Textarea
            placeholder={t('settings.note.instructionsPlaceholder')}
            className="min-h-55 resize-none border-0 bg-transparent shadow-none ring-0 ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </div>

        <Stack direction="horizontal" justify="end" align="center" gap={2}>
          <Button
            variant="ghost"
            size="sm"
            disabled={!isDirty || isSaving}
            onClick={() => setValue(customInstructions)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!isDirty || isSaving}
            loading={isSaving}
            onClick={handleSave}
          >
            {t('settings.common.save')}
          </Button>
        </Stack>
      </Stack>

      <Stack gap={2} align="start">
        <Caption
          size="3xs"
          weight="bold"
          color="supporting"
          className="block tracking-[2px]"
        >
          {t('settings.common.examples')}
        </Caption>
        <Stack gap={2} align="start">
          {CHAT_EXAMPLE_INSTRUCTION_KEYS.map((exampleKey) => (
            <Button
              key={exampleKey}
              variant="outline"
              size="sm"
              onClick={() => setValue(t(exampleKey))}
            >
              {t(exampleKey)}
            </Button>
          ))}
        </Stack>
      </Stack>
    </SettingsPageFrame>
  );
};
